import { AppConfig as env, CenosApp, CenosFrontendVersion } from '../config/environment.js';
import {remote} from 'electron';

const isDevEnv = (env.name === 'dev');
const isTestEnv = (env.name === 'test');
const isProdEnv = (env.name === 'prod');

const jetpack = require('fs-jetpack');
const os = require('os');
const path = require('path');

const isRenderer = (process && process.type === 'renderer');
const app = (isRenderer ? remote.app : require('electron').app);

// App and config dir depends on environment.
const appDir = (isDevEnv) ? jetpack.cwd(app.getAppPath()) : jetpack.cwd(app.getPath('exe')).cwd('..');
const backendDir = appDir.cwd(env.backend.dir);
const backendConfigDir = (isDevEnv) ? backendDir.cwd('app', CenosApp) : backendDir.cwd('app');

// Directories (as fs-jetpack objects).
const userDir = jetpack.cwd(os.homedir());
const systemTempDir = jetpack.cwd(require('os').tmpdir());

// Read CENOS app config.
const appConfigPath = backendConfigDir.path('jsons', '_generated', 'app.json');
const classifiersPath = backendConfigDir.cwd('jsons').path('classifiers.json');
const appConfig = jetpack.read(appConfigPath, 'json');
const classifiers = jetpack.read(classifiersPath, 'json');

// Fatal errors.
if (backendConfigDir.exists('jsons') !== 'dir') {
    throw new Error('Check backend path in config. Is the following correct? ' + backendDir.cwd());
} else if (!CenosApp) {
    throw new Error('CenosApp is not defined');
} else if (!appConfig) {
    throw new Error(`App config is not found: ${appConfigPath}`);
} else if (!classifiers) {
  throw new Error(`Classifiers are not found: ${classifiersPath}`);
}

// Global constants.
let constants = {
    isDevEnv: isDevEnv,
    isTestEnv: isTestEnv,
    isProdEnv: isProdEnv,

    isMainProcess: (process && process.type === 'browser'),
    isRenderer: (process && process.type === 'renderer'),
    isWorker: (process && process.type === 'worker'),

    // CENOS app shortcuts. Defined below.
    // Note: Use as little as possible. Put into CENOS app configuration if possible.
    isAntennas: undefined,
    isInduction: undefined,

    app: {
        dir: appDir.cwd(),

        assetDir: (isDevEnv) ? appDir.path('renderer', 'assets') : path.join(process.resourcesPath, 'app.asar', 'app', 'renderer', 'assets'),
        configDir: userDir.path(`.cenos-${CenosApp}`),
        usageDir:  userDir.path(`.cenos-${CenosApp}`, 'usage'),

        resourcesDir: (isDevEnv) ? appDir.path('..', 'resources') : process.resourcesPath,
        userDir: userDir.path('CENOS'),
        tempDir: systemTempDir.path('cenos'),

        config: appConfig.app,

        caseFilename: 'case.json',
        configFilename: 'config.json',
        recentCasesFilename: 'recent-cases.json',
        recentDataFilename: 'recent-data.json',
        recentTemplateFilename: 'recent-template-case.json',
        userExpressionsFilename: 'user-expressions.json',
        userMaterialsFilename: 'user-materials.json',
        userValueLibraryFilename: 'user-value-library.json',

        recentDataFilenameBackup: 'recent-data.json.bak',
        userExpressionsFilenameBackup: 'user-expressions.json.bak',
        userMaterialsFilenameBackup: 'user-materials.json.bak',
        userValueLibraryFilenameBackup: 'user-value-library.json.bak',

        cenosApp: CenosApp, // 'INDUCTION', 'ANTENNAS', in dev: 'ALL'
        isBeta: app.getVersion().includes('beta'),
        name: app.name, // 'CENOS Induction Heating', 'CENOS Antenna Design', in dev: 'CENOS Platform'

        appVersion: app.getVersion(),
        frontendVersion: CenosFrontendVersion,
    },
    backend: {
        binDir: backendDir.path('bin'),
        configDir: backendConfigDir.cwd(),
        dir: backendDir.cwd(),
        path: backendDir.path('bin', 'cenos-backend.exe'),
    },
    case: {
        previewDir: 'preview',
        previewGeometryDir: 'preview_geometry',
    },
    DELIMITER: path.delimiter,
    EOL: require('os').EOL,
    env: env,
    path: {
        zip7: (isDevEnv) ? require('7zip-bin').path7za : backendDir.path('7za.exe'),
    },
    SLASH: path.sep,
    url: {
        // events: 'http://localhost:3000/events/',
        events: 'https://server.cenos-platform.com/events',
        feedback: 'http://94.130.75.44/feedback/',
    },

    BlockType: {
        PRE: 'pre',
        POST: 'post',
        PHYSICS: 'physics',
    },

    // Special boundary types.
    BoundaryType: {
        INTERFACE: 'interface',
    },

    BugType: {
        BUG: 'bug',
        IMPROVEMENT: 'improvement',
        FEATURE: 'feature',
    },
    classifiers: classifiers,
    CenosApp: {
        ALL: 'ALL',
        ANTENNAS: 'ANTENNAS',
        API: 'API',
        INDUCTION: 'INDUCTION',
    },
    Editor: {
        FREE_CAD: 'freecad',
        SALOME: 'salome',
    },
    EntityType: {
        BOUNDARY: 'boundary',
        DOMAIN: 'domain',
    },
    GeometryUnits: [
        {key: 'm', unit: 'm', name: 'metre', factor: 1},
        {key: 'cm', unit: 'cm', name: 'centimetre', factor: 100},
        {key: 'mm', unit: 'mm', name: 'millimeter', factor: 1000},
        {key: 'in', unit: 'in', name: 'inch', factor: 39.3700787},
        {key: 'ft', unit: 'ft', name: 'feet', factor: 3.280839895},
    ],
    GeometryGroupMode: {
        FROM_PREPROCESSOR: 'groupsFromPreprocessor',
        FROM_ALL_ENTITIES: 'groupsFromAllEntities',
    },
    InstallerType: {
        EXE: 'exe',
        EXE_7ZIP: 'exe-7zip',
        ZIP: 'zip',
        ZIP7: '7zip',
    },
    MaterialSourceType: {
        NONE: 'none',
        LIBRARY: 'library',
        USER: 'userLibrary',
        CUSTOM: 'custom',
    },
    MessageType: {
        LOG: 'log',
        RESPONSE: 'response',
        WAIT: 'wait',
    },
    MeshDensity: {
        AVERAGE: 'average',
        FINE: 'fine',
        ROUGH: 'rough',
    },
    // For tweaking. Try to avoid using it.
    PhysicsBlock: {
        ELECTROMAGNETICS: 'physicsElectromagnetics',
        INDUCTION: 'physicsInductionHeating',
        MICROWAVES: 'physicsMicrowaves',
        THERMAL: 'physicsThermal',
    },
    PreProcessor: {
        GEOM_TEMPLATES: 'geomWizard',
        GEOM_FROM_CAD: 'geomFromCad',
        GEOM_ROLES: 'geomRoles',
        GEOM_ROLES_CAD: 'geomRolesCad',
        SALOME: 'salome',
        SALOME_FROM_CAD: 'salomeCad',
        MESH_FILE: 'read',
    },
    PostProcessStatus: {
        CLOSED: 'closed',
        STARTING: 'starting',
        RUNNING: 'running',
    },
    ResponseType: {
        SUCCESS: 'success',
        ERROR: 'error',
    },
    programs: {},
    RunProcessStatus: {
        STARTED: 0,
        STOPPED: 1,
        ERROR: 2,
        FINISHED: 3,
    },
    Role: {
        AIR: 'air',
        AXIS: 'axis',
        INDEFINITE: 'indefinite',
        INFINITY: 'infinity',
    },
    RunStatus: {
        UNREADY: 'unready',
        READY: 'ready',
        RUNNING: 'running',
        PAUSED: 'paused',
    },
    UsageEvent: {
        ALERT: 'alert',
        ANSWER: 'answer',
        BACKEND: 'backend',
        CLICK: 'click',
        CONFIRM: 'confirm',
        DIALOG: 'dialog',
        KEY: 'key',
        MATERIAL: 'material',
        PAGE: 'page',
        RESIZE: 'resize',
    },
    ws: {
        url: 'ws://[::1]',
        id: 'gui',
        port: 8089,
    },

    // WebSocket readyState as per specification.
    WsReadyState: {
        CONNECTING: 0,
        OPEN: 1,
        CLOSING: 2,
        CLOSED: 3,
    }
};

// CENOS app shortcuts.
// Note: Use as little as possible. Put into CENOS app configuration if possible.
constants.isAntennas = (constants.app.cenosApp === constants.CenosApp.ANTENNAS);
constants.isInduction = (constants.app.cenosApp === constants.CenosApp.INDUCTION);

// Third-party programs.
const programs = {
    vcpp: {
        checkForFile: false,
        checkInRegistry: true,
        cliArguments: '/install', // this will prompt user to install MSVC redistributables
                                  // also there will be warning if restart is needed
        description: 'Required',
        downloadLink: 'https://aka.ms/vs/16/release/vc_redist.x64.exe',
        hideIfInstalled: true,
        name: 'Microsoft VC++ Redistributable',
        note: 'Required',
        registryHive: 'HKLM',
        registryKey:  '\\SOFTWARE\\WOW6432Node\\Microsoft\\VisualStudio\\14.0\\VC\\Runtimes\\X64',
        registryItem: {name: 'Minor', minValue: 25},
        type: constants.InstallerType.EXE,
    },
    getdp: {
        checkForFile: true,
        checkInRegistry: false,
        description: 'Generic FEM tool for thermal and electromagnetic calculations',
        downloadLink: 'https://server.cenos-platform.com/download/getdp-3.4.1-cenos-d62d3083.7z',
        fileName: 'getdp.exe',
        licenseLink: 'http://getdp.info/#Licensing',
        name: 'GetDP',
        restrictPath: true,
        rootDir: 'getdp-3.4.*',
        subDir: null,
        type: constants.InstallerType.ZIP7,
        version: {
            args: '-version 2>&1', // Version is printed in stderr.
            command: 'getdp',
            min: '3.4.1',
            regex: /\d+\.\d+\.\d+/,
        },
    },
    paraview: {
        checkForFile: true,
        checkInRegistry: false,
        description: 'Post-processing (visualization) tool',
        downloadLink: 'https://www.paraview.org/paraview-downloads/download.php?submit=Download&version=v5.9&type=binary&os=Windows&downloadFile=ParaView-5.9.0-Windows-Python3.8-msvc2017-64bit.zip',
        fileName: 'paraview.exe',
        licenseLink: 'https://www.paraview.org/paraview-license/',
        name: 'ParaView 5.9',
        restrictPath: false,
        rootDir: 'ParaView-5.*',
        subDir: 'bin',
        type: constants.InstallerType.ZIP,
        version: {
            args: '--version',
            command: 'bin/pvbatch',
            min: '5.9',
            regex: /\d+\.\d+\.\d+/,
        },
    },
};

// Require Salome.
if ((CenosApp === constants.CenosApp.ALL) || (CenosApp === constants.CenosApp.INDUCTION) || (CenosApp === constants.CenosApp.API)) {
    Object.assign(programs, {
        salome: {
            checkForFile: true,
            checkInRegistry: false,
            description: 'Pre-processing tool for geometry creation and meshing',
            downloadLink: 'https://www.salome-platform.org/downloads/current-version/DownloadDistr?platform=WinBin64Exe&version=9.3.0',
            fileName: 'run_salome.bat',
            licenseLink: 'https://www.salome-platform.org/downloads/license',
            name: 'Salome 9',
            restrictPath: true,
            rootDir: 'SALOME-9.*',
            subDir: null,
            type: constants.InstallerType.EXE_7ZIP,
        },
    });
}

// Require FreeCAD.
if ((CenosApp === constants.CenosApp.ALL) || (CenosApp === constants.CenosApp.ANTENNAS)) {
    Object.assign(programs, {
        freecad: {
            checkForFile: true,
            checkInRegistry: false,
            description: 'Generic FEM tool for thermal and electromagnetic calculations',
            downloadLink: 'https://github.com/FreeCAD/FreeCAD/releases/download/0.19.1/FreeCAD_0.19.24276-Win-Conda_vc14.x-x86_64.7z',
            fileName: 'FreeCAD.exe',
            licenseLink: 'https://wiki.freecadweb.org/Licence',
            name: 'FreeCAD',
            restrictPath: false,
            rootDir: 'FreeCAD_0.19.*',
            subDir: 'bin',
            type: constants.InstallerType.ZIP7,
            version: {
                args: '--version',
                command: 'bin/freecadcmd',
                min: '0.19.24276',
                regex: /\d+\.\d+([^\d]+)\d+/,
            },
        }
    });
}

constants.programs = programs;

export default constants;
